import { useEffect, useState, useCallback } from "react";
import { Siren, MapPin, ExternalLink, Trash2, Phone, Mail, User, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

export function DepannagesView() {
  const { t } = useLang();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/depannage"); setAlerts(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const remove = async (a) => {
    if (!window.confirm(t("admin.dep_delete_confirm"))) return;
    try { await api.delete(`/depannage/${a.id}`); setAlerts((p) => p.filter((x) => x.id !== a.id)); toast.success(t("admin.dep_deleted")); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="depannage-view">
      <div className="flex items-center gap-2 mb-6">
        <Siren className="w-5 h-5 text-[#E60000]" />
        <h2 className="font-display font-900 text-white text-xl uppercase tracking-tight">{t("admin.dep_title")}</h2>
        {alerts.length > 0 && <span className="ml-1 px-2 py-0.5 bg-[#E60000] text-white font-display font-900 text-xs rounded-full">{alerts.length}</span>}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : alerts.length === 0 ? (
        <div className="border border-white/10 bg-[#0C0C0E] py-20 text-center" data-testid="depannage-empty">
          <Siren className="w-8 h-8 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-500 font-mono-tech text-sm">{t("admin.dep_empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((a) => (
            <div key={a.id} data-testid={`depannage-item-${a.id}`} className="border border-[#E60000]/30 bg-[#0C0C0E] p-5 flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="w-8 h-8 flex items-center justify-center bg-[#E60000]/15 border border-[#E60000]/40"><Siren className="w-4 h-4 text-[#E60000]" /></span>
                  <span className="font-mono-tech text-[11px] uppercase tracking-[0.15em] text-zinc-400">{fmt(a.created_at)}</span>
                </div>
                <div className="flex items-center gap-2 text-white font-display font-700"><User className="w-4 h-4 text-zinc-500" /> {a.name || <span className="text-zinc-500 italic font-sans font-400">{t("admin.dep_anon")}</span>}</div>
                <div className="flex items-center gap-4 mt-1.5 flex-wrap font-mono-tech text-xs text-zinc-400">
                  {a.phone && <a href={`tel:${a.phone}`} className="flex items-center gap-1 hover:text-[#00E5FF]"><Phone className="w-3.5 h-3.5" /> {a.phone}</a>}
                  {a.email && <a href={`mailto:${a.email}`} className="flex items-center gap-1 hover:text-[#00E5FF]"><Mail className="w-3.5 h-3.5" /> {a.email}</a>}
                </div>
                <div className="mt-2.5">
                  {a.maps_url ? (
                    <a href={a.maps_url} target="_blank" rel="noreferrer" data-testid={`depannage-maps-${a.id}`} className="inline-flex items-center gap-2 px-3 py-2 bg-[#00E5FF]/10 border border-[#00E5FF]/40 text-[#00E5FF] font-mono-tech text-[11px] uppercase tracking-[0.12em] hover:bg-[#00E5FF] hover:text-black transition-colors">
                      <MapPin className="w-3.5 h-3.5" /> {t("admin.dep_open_maps")} <ExternalLink className="w-3 h-3" />
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-zinc-500 font-mono-tech text-[11px]"><MapPin className="w-3.5 h-3.5" /> {t("admin.dep_no_position")}</span>
                  )}
                </div>
              </div>
              <button onClick={() => remove(a)} data-testid={`depannage-delete-${a.id}`} className="w-9 h-9 flex items-center justify-center border border-white/10 text-zinc-500 hover:border-[#E60000] hover:text-[#E60000] transition-colors shrink-0">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
